/**
 * A module that coordinates running A/B tests.
 * @module abt-core/abtest-runner
 */

import _ from 'lodash'
import $ from 'jquery'
import cookies from '../abt-utils/cookies'
import siteConfig from './site-config.json'
import * as analytics from '../abt-utils/analytics'
import * as device from '../abt-utils/device'
import * as qa from './qa'
import * as queryString from '../abt-utils/query-string'
import * as validate from './validate'

/**
 * Creates a test recipe cookie for the user if one does not already exist.
 * @param {object} test - The test config being evaluated.
 * @returns {object} Returns the test config being evaluated.
 */
export function createCookie (test) {
  if (_isScaled(test) || _isBackend(test)) {
    return test
  }

  var cookie = _getTestCookie(test)
  if (cookie && !qa.includes(test)) {
    return test
  }

  var version = _getTestRecipe(test) || _generateTestRecipe(test)
  var expiration = _getTestCookieExpiration(test)
  cookies.set(test.id, version, expiration)

  return test
}

/**
 * Evaluates common criteria against the current page metadata.
 * @param {object} test - The test config being evaluated.
 * @returns {object|undefined} Returns the test config or undefined if validation fails.
 */
export function criteria (test) {
  return validate.all(test) ? test : undefined
}

/**
 * Evaluates custom criteria against the current page.
 * @param {object} test - The test config being evaluated.
 * @returns {object|promise} Returns the current test config if no custom criteria exists or a
 * promise that resolves with the test config when the custom criteria function has completed.
 */
export function customCriteria (test) {
  var deviceSize = device.getSize()
  if (!test[deviceSize] || !test[deviceSize].customCriteria) return test

  return test[deviceSize].customCriteria(test)
}

/**
 * Runs the test recipe function associated with the user's test cookie.
 * @param {object} test - The test config being evaluated.
 * @returns {object|promise} Returns the current test config if no execution function exists or a
 * promise that resolves with the test config when the recipe function has completed.
 */
export function runTestRecipe (test) {
  var deviceSize = device.getSize()
  var version = _getTestRecipe(test)
  if (!test[deviceSize] || !test[deviceSize][version]) return test

  $('body').addClass(test.id + '-' + version)
  return test[deviceSize][version](test)
}

/**
 * Sets metrics data for the test that's currently running.
 * @param {object} test - The test config being evaluated.
 * @returns {object} Returns the test config being evaluated.
 */
export function setMetricsData (test) {
  var evar = test.evar || {}

  if (!evar.skip && !_isScaled(test)) {
    analytics.sendEvar(test)
  }

  return test
}

/**
 * Appends LID values for the test that's currently running.
 * @param {object} test - The test config being evaluated.
 * @returns {object} Returns the current test config being evaluated.
 */
export function appendLids (test) {
  if (!_isScaled(test)) {
    analytics.appendLids(test)
  }

  return test
}

/**
 * Gets the test recipe assigned to the user.
 *
 * If the recipe is definied in the url, always use this value. This is how we QA our tests
 * and ensure our tests in production are working as expected. If the test is scaled, it should
 * always return `scaled` as the test recipe. If neither of these cases apply, the test recipe
 * should be retrieved from the user's test cookie.
 *
 * @param {object} test - The test config being evaluated.
 * @returns {string} Returns the test recipe.
 */
export function _getTestRecipe (test) {
  var testRecipe = queryString.get(test.id)

  if (testRecipe) {
    // use the recipe in the url, if specified
    return testRecipe
  } else if (_isScaled(test)) {
    // use test type, if the test is scaled
    return test.type
  }

  // use recipe from test cookie
  return _getTestCookie(test)
}

/**
 * Assigns a random test recipe to the user.
 *
 * The test recipe is dynamically created based on the number of splits.  The splitWeight is also
 * accounted for if specified.
 *
 * @param {object} test - The test config being evaluated.
 * @returns {string} Returns the randomly assigned test recipe.
 */
export function _generateTestRecipe (test) {
  var upperLimit = 1000
  var value = _.random(upperLimit)
  var evenSplit = 100 / test.split

  // TODO: replace custom _fill -> _.fill when lodash 3.10.1 is available.
  var splitWeight = test.splitWeight || _fill(Array(test.split), evenSplit)

  var accumulator = 0

  for (var i = 1; i <= test.split; i++) {
    var isLastSplit = i === test.split
    var remainder = upperLimit - accumulator
    var calculatedWeight = splitWeight[i - 1] * 10

    accumulator += Math.round(isLastSplit ? remainder : calculatedWeight)

    if (value > accumulator) {
      continue
    }

    return String.fromCharCode(i + 96)
  }
}

/**
 * Gets the user's test cookie.
 * @param {object} test - The test config being evaluated.
 * @returns {string} Returns the value assigned to the user's test cookie.
 */
function _getTestCookie (test) {
  return cookies.get(test.id)
}

/**
 * Gets the test cookie expiration date.
 * @param {object} test - The test config being evaluated.
 * @returns {date} Returns the cookie expirate date.
 */
function _getTestCookieExpiration (test) {
  if (!test.cookieExpiration) {
    throw new Error('Missing cookie expiration date for ' + test.id)
  }

  return new Date(test.cookieExpiration)
}

/**
 * Determines if the test is scaled.
 * @param {object} test - The test config being evaluated.
 * @returns {boolean} Returns TRUE if the test is scaled, otherwise FALSE.
 */
function _isScaled (test) {
  return test.type === siteConfig.testType.SCALED
}

/**
 * Determines if this is a backend test.
 * @param {object} test - The test config being evaluated.
 * @returns {boolean} Returns TRUE if it is a backend test, otherwise FALSE.
 */
function _isBackend (test) {
  return test.type === siteConfig.testType.BACKEND
}

/** Temporary fix until lodash 3.10.1 is available. */
function _fill (array, evenSplit) {
  return _.map(array, function () {
    return evenSplit
  })
}



/*****************
 ** WEBPACK FOOTER
 ** ./src/abt-core/abtest-runner.js
 ** module id = 15
 ** module chunks = 2 3
 **/